import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faFilter, faChevronDown, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { crmService } from '../../services/crmService';
import { fetchHMSEntities } from '../../services/hmsEntityService';
import invoicingService from '../../services/invoicingService';
import SearchableSelect from './SearchableSelect';
import './InvoiceSearchFilter.css';
import { costCentreLabel } from '../../utils/costCentreLabel';

/**
 * Invoice Search and Filter Component
 * 
 * Reusable search component for all invoice types
 * Matches styling and functionality of StockItemsSearch component
 * 
 * Props:
 * - onSearchResults: callback with search results array
 * - onFiltersChange: callback with active filters object
 * - isSearching: boolean loading state
 * - onSearchStateChange: callback to notify parent of search state
 * - onClearSearch: callback to exit search mode
 * - invoiceType: 'incoming', 'outgoing', 'proforma', 'purchase-order', 'credit_note', or 'debit_note'
 */

const InvoiceSearchFilter = ({ 
    onSearchResults, 
    onFiltersChange, 
    isSearching, 
    onSearchStateChange, 
    onClearSearch,
    invoiceType 
}) => {
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        hms_entity: '',
        partner: '',
        cost_center: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    // null when the last search answered. Otherwise why it did not -- refused,
    // truncated, or broken. Rendered as an alert above the filters so that no
    // failure can reach the user dressed as "no results found".
    const [searchProblem, setSearchProblem] = useState(null);
    
    // Data for dropdowns
    const [hmsEntities, setHmsEntities] = useState([]);
    const [partners, setPartners] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    
    // Partner autocomplete state
    const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);
    const [partnerSearchTerm, setPartnerSearchTerm] = useState('');
    const [filteredPartners, setFilteredPartners] = useState([]);
    const partnerDropdownRef = useRef(null);
    const partnerInputRef = useRef(null);
    
    // Track previous query to detect actual user changes (not initial render)
    const prevQueryRef = useRef(query);
    const isInitialMount = useRef(true);

    // Status options based on invoice type
    const getStatusOptions = () => {
        switch(invoiceType) {
            case 'outgoing':
                return [
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                    { value: 'APPROVED', label: 'Approved' },
                    { value: 'SENT', label: 'Sent' },
                    { value: 'PAID', label: 'Paid' },
                    { value: 'OVERDUE', label: 'Overdue' },
                    { value: 'CANCELLED', label: 'Cancelled' },
                    { value: 'VOIDED', label: 'Voided' }
                ];
            case 'proforma':
                return [
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                    { value: 'APPROVED', label: 'Approved' },
                    { value: 'SENT', label: 'Sent' },
                    { value: 'CANCELLED', label: 'Cancelled' }
                ];
            case 'incoming':
                return [
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                    { value: 'APPROVED', label: 'Approved' },
                    { value: 'PAID', label: 'Paid' },
                    { value: 'OVERDUE', label: 'Overdue' },
                    { value: 'CANCELLED', label: 'Cancelled' },
                    { value: 'VOIDED', label: 'Voided' }
                ];
            case 'purchase-order':
                return [
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                    { value: 'APPROVED', label: 'Approved' },
                    { value: 'SENT', label: 'Sent' },
                    { value: 'CONFIRMED', label: 'Confirmed' },
                    { value: 'PARTIALLY_RECEIVED', label: 'Partially Received' },
                    { value: 'FULLY_RECEIVED', label: 'Fully Received' },
                    { value: 'CLOSED', label: 'Closed' },
                    { value: 'CANCELLED', label: 'Cancelled' }
                ];
            case 'credit_note':
            case 'debit_note':
                return [
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                    { value: 'APPROVED', label: 'Approved' },
                    { value: 'REJECTED', label: 'Rejected' }
                ];
            default:
                return [];
        }
    };

    // Get placeholder text based on invoice type
    const getPlaceholderText = () => {
        switch(invoiceType) {
            case 'outgoing':
                return 'Search by invoice number, partner name, IMT entity...';
            case 'proforma':
                return 'Search by pro-forma number, partner name, IMT entity...';
            case 'incoming':
                return 'Search by invoice/expense number, supplier, IMT entity...';
            case 'purchase-order':
                return 'Search by PO number, supplier name, IMT entity...';
            case 'credit_note':
                return 'Search by credit note number, partner name, IMT entity...';
            case 'debit_note':
                return 'Search by debit note number, supplier name, IMT entity...';
            default:
                return 'Search...';
        }
    };

    // Load initial data - service-level caching prevents duplicate calls in React StrictMode
    useEffect(() => {
        let isMounted = true;
        
        const loadData = async () => {
            try {
                setLoadingData(true);
                
                // Load HMS entities and partners in parallel
                // Services now have built-in caching to prevent duplicate API calls
                const [entitiesData, partnersData] = await Promise.all([
                    fetchHMSEntities(),
                    crmService.getPartnerData()
                ]);
                
                if (isMounted) {
                    setHmsEntities(entitiesData || []);
                    setPartners(partnersData.sort((a, b) => a.name.localeCompare(b.name)));
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error loading search data:', error);
                }
            } finally {
                if (isMounted) {
                    setLoadingData(false);
                }
            }
        };

        loadData();
        
        // Cleanup function to prevent state updates on unmounted component
        return () => {
            isMounted = false;
        };
    }, []);

    // Handle outside clicks for partner dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(event.target)) {
                setShowPartnerDropdown(false);
                setPartnerSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter partners based on search term
    useEffect(() => {
        if (!partnerSearchTerm.trim()) {
            setFilteredPartners([]);
        } else {
            const filtered = partners.filter(partner =>
                partner.name.toLowerCase().includes(partnerSearchTerm.toLowerCase())
            );
            setFilteredPartners(filtered.slice(0, 10)); // Limit to 10 results
        }
    }, [partnerSearchTerm, partners]);

    // Debounced search - only for text query, not filters
    useEffect(() => {
        // Skip on initial mount - only trigger on actual user interaction
        if (isInitialMount.current) {
            isInitialMount.current = false;
            prevQueryRef.current = query;
            return;
        }
        
        const timer = setTimeout(() => {
            // Only auto-search for text queries, not filters
            if (query.length >= 2) {
                performSearch();
            } else if (query.length === 0 && prevQueryRef.current.length > 0) {
                // User cleared the search (went from having text to empty)
                const hasActiveFilters = Object.values(filters).some(value => value !== '');
                if (!hasActiveFilters) {
                    // EMPTY SEARCHBAR AND NO FILTERS = CLEAR SEARCH MODE DIRECTLY
                    console.log('USER CLEARED SEARCH - SHOWING ALL ITEMS');
                    onSearchStateChange(false);
                    onFiltersChange({});
                    if (onClearSearch) {
                        onClearSearch();
                    }
                }
            }
            prevQueryRef.current = query;
        }, 300);

        return () => clearTimeout(timer);
    }, [query]); // Removed filters from dependency array

    const performSearch = async () => {
        const hasQuery = query.length >= 2;
        const hasFilters = Object.values(filters).some(value => value !== '');
        
        console.log('performSearch called:', { hasQuery, hasFilters, activeFiltersCount: Object.values(filters).filter(Boolean).length });
        
        // If no query and no filters, clear search mode directly without empty results
        if (!hasQuery && !hasFilters) {
            console.log('No query and no filters - clearing search mode directly');
            // Nothing was asked, so nothing failed: a stale warning from the
            // previous search would be its own small lie.
            setSearchProblem(null);
            onSearchStateChange(false);
            onFiltersChange({});
            // Signal to parent to exit search mode and load all items (no empty state)
            if (onClearSearch) {
                onClearSearch();
            }
            return;
        }

        onSearchStateChange(true);
        
        try {
            const params = new URLSearchParams();
            
            if (hasQuery) {
                params.append('search', query);
            }
            
            // Add filters to params, mapping 'partner' to 'supplier' for purchase orders
            Object.entries(filters).forEach(([key, value]) => {
                if (value) {
                    if (key === 'partner' && invoiceType === 'purchase-order') {
                        params.append('supplier', value);
                    } else {
                        params.append(key, value);
                    }
                }
            });

            // The endpoint map and the list-completeness guard both live in
            // invoicingService. This component used to hold its own copy of the
            // six URLs and call axios directly, which meant the search bar was
            // the one control on these screens that walked straight past the
            // guard -- on the very screens the guard was added to protect.
            const rows = await invoicingService.searchInvoiceList(invoiceType, params);
            console.log('Search results:', rows.length, 'items found');

            setSearchProblem(null);
            // Update filters FIRST so parent has correct activeFilters state when processing results
            onFiltersChange(filters);
            onSearchResults(rows);
        } catch (error) {
            // A failed search is NOT "no matches". Handing the parent [] here
            // put the screen straight into its empty state and told the user
            // their search found nothing -- for a refusal, a dropped connection
            // or a half-loaded page alike. The rows on screen are left exactly
            // as they were and the reason is shown instead.
            console.error('Search API error:', error);
            console.error('Error response:', error.response?.data);
            setSearchProblem(
                invoicingService.describeListFailure(error, 'Your search'),
            );
        } finally {
            // Only signal search is done, but don't exit search mode if we have results/filters
            onSearchStateChange(false);
            console.log('Search completed');
        }
    };

    const handleFilterChange = (filterName, value) => {
        console.log('Filter changed:', filterName, '=', value);
        setFilters(prev => {
            const newFilters = {
                ...prev,
                [filterName]: value
            };
            console.log('New filters state:', newFilters);
            return newFilters;
        });
    };

    const handlePartnerSelect = (partner) => {
        handleFilterChange('partner', partner.id);
        setShowPartnerDropdown(false);
        setPartnerSearchTerm('');
    };

    const clearFilters = () => {
        setSearchProblem(null);
        setFilters({
            status: '',
            hms_entity: '',
            partner: '',
            cost_center: ''
        });
        setQuery('');
    };

    const activeFiltersCount = Object.values(filters).filter(Boolean).length;

    return (
        <div className="invoice-search-filter">
            {/* AC7: a search that was refused, or that came back as page one of
                many, must never be drawn as "no results". This banner is the
                only thing standing between those two answers and the empty
                table underneath. */}
            {searchProblem && (
                <div
                    role="alert"
                    className="search-load-problem"
                    style={{
                        margin: '0 0 0.75rem',
                        padding: '0.65rem 0.9rem',
                        border: '1px solid #f59e0b',
                        borderLeft: '4px solid #b45309',
                        borderRadius: '4px',
                        background: '#fffbeb',
                        color: '#78350f',
                        fontSize: '0.85rem',
                    }}
                >
                    <strong style={{ marginRight: '0.35rem' }}>
                        {searchProblem.kind === 'truncated'
                            ? 'Your search found more than this screen can show.'
                            : 'Your search could not be run.'}
                    </strong>
                    {searchProblem.message}
                </div>
            )}
            <div className="search-header">
                <div className="search-input-group">
                    <div className="search-input-container">
                        <FontAwesomeIcon 
                            icon={isSearching ? faSpinner : faSearch} 
                            className={`search-icon ${isSearching ? 'fa-spin' : ''}`}
                        />
                        <input
                            type="text"
                            placeholder={getPlaceholderText()}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="search-input compact"
                        />
                        {query && (
                            <button
                                className="clear-search"
                                onClick={() => setQuery('')}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        )}
                    </div>
                    
                    <button
                        className={`filter-toggle-button ${showFilters ? 'active' : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <FontAwesomeIcon icon={faFilter} />
                        Filters
                        {activeFiltersCount > 0 && (
                            <span className="filter-count">{activeFiltersCount}</span>
                        )}
                        <FontAwesomeIcon 
                            icon={faChevronDown} 
                            className={`chevron ${showFilters ? 'rotated' : ''}`}
                        />
                    </button>
                </div>
            </div>

            {showFilters && (
                <div className="search-filters-panel">
                    <div className="filters-grid">
                        {/* Status Filter */}
                        <div className="form-group">
                            <label>Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="form-select"
                                disabled={loadingData}
                            >
                                <option value="">All Statuses</option>
                                {getStatusOptions().map(status => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* HMS Entity Filter */}
                        <div className="form-group">
                            <label>IMT Entity</label>
                            <select
                                value={filters.hms_entity}
                                onChange={(e) => {
                                    handleFilterChange('hms_entity', e.target.value);
                                    handleFilterChange('cost_center', '');
                                }}
                                className="form-select"
                                disabled={loadingData}
                            >
                                <option value="">All Entities</option>
                                {hmsEntities.map(entity => (
                                    <option key={entity.value} value={entity.value}>
                                        {entity.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Cost Center Filter */}
                        <div className="form-group">
                            <label>Cost Center</label>
                            <SearchableSelect
                                fetchUrl="/api/invoicing/cost-centers/"
                                params={filters.hms_entity ? { entity: filters.hms_entity } : {}}
                                displayFn={costCentreLabel}
                                value={filters.cost_center}
                                onChange={(id) => handleFilterChange('cost_center', id || '')}
                                placeholder="All Cost Centers"
                                disabled={loadingData}
                                variant="form"
                            />
                        </div>

                        {/* Partner Filter */}
                        <div className="form-group" ref={partnerDropdownRef}>
                            <label>{(invoiceType === 'outgoing' || invoiceType === 'proforma' || invoiceType === 'credit_note') ? 'Partner' : 'Supplier'}</label>
                            <div className="partner-select-container">
                                <div 
                                    className={`crm-modern-multi-select-header compact ${showPartnerDropdown ? 'active' : ''}`}
                                    onClick={() => {
                                        if (!loadingData) {
                                            setShowPartnerDropdown(!showPartnerDropdown);
                                            if (!showPartnerDropdown) {
                                                setTimeout(() => {
                                                    if (partnerInputRef.current) {
                                                        partnerInputRef.current.focus();
                                                    }
                                                }, 150);
                                            }
                                        }
                                    }}
                                >
                                    <span>
                                        {filters.partner 
                                            ? partners.find(p => p.id === parseInt(filters.partner))?.name || 'Select...'
                                            : (invoiceType === 'outgoing' || invoiceType === 'proforma' || invoiceType === 'credit_note') ? 'All Partners' : 'All Suppliers'}
                                    </span>
                                    <FontAwesomeIcon icon={faChevronDown} className="dropdown-arrow" />
                                </div>
                                
                                {showPartnerDropdown && (
                                    <div className="crm-modern-multi-select-dropdown compact">
                                        <div className="crm-search-input-container">
                                            <input
                                                ref={partnerInputRef}
                                                type="text"
                                                className="crm-search-input"
                                                placeholder={`Search ${(invoiceType === 'outgoing' || invoiceType === 'proforma' || invoiceType === 'credit_note') ? 'partners' : 'suppliers'}...`}
                                                value={partnerSearchTerm}
                                                onChange={(e) => setPartnerSearchTerm(e.target.value)}
                                                autoFocus
                                            />
                                        </div>

                                        <div className="crm-modern-multi-select-options">
                                            <div 
                                                className="crm-modern-multi-select-option"
                                                onClick={() => {
                                                    handleFilterChange('partner', '');
                                                    setShowPartnerDropdown(false);
                                                    setPartnerSearchTerm('');
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <span>{(invoiceType === 'outgoing' || invoiceType === 'proforma' || invoiceType === 'credit_note') ? 'All Partners' : 'All Suppliers'}</span>
                                            </div>
                                            
                                            {!partnerSearchTerm.trim() ? (
                                                <div className="crm-modern-multi-select-option">
                                                    <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                        Start typing to search...
                                                    </span>
                                                </div>
                                            ) : filteredPartners.length > 0 ? (
                                                filteredPartners.map((partner) => (
                                                    <div 
                                                        key={partner.id} 
                                                        className="crm-modern-multi-select-option"
                                                        onClick={() => handlePartnerSelect(partner)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <span>{partner.name}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="crm-modern-multi-select-option">
                                                    <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                        No {(invoiceType === 'outgoing' || invoiceType === 'proforma' || invoiceType === 'credit_note') ? 'partners' : 'suppliers'} found matching "{partnerSearchTerm}"
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="filters-actions">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                                console.log('Apply button clicked!');
                                console.log('Current filters state:', filters);
                                console.log('Current query state:', query);
                                console.log('Active filters count:', Object.values(filters).filter(Boolean).length);
                                performSearch();
                                setShowFilters(false); // Close filters when apply is pressed
                            }}
                            disabled={isSearching}
                        >
                            {isSearching ? 'Applying...' : 'Apply'}
                        </button>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={clearFilters}
                            disabled={query === '' && activeFiltersCount === 0}
                        >
                            Clear All
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoiceSearchFilter;

